/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    baseStyle: {
        borderRadius: 'none',
        selectedSortLink: {
            position: 'relative',
            cursor: 'auto',
            _before: {
                content: '""',
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M16.6634 5L7.49674 14.1667L3.33008 10' stroke='black' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat center center/20px;`,
                width: '20px',
                height: '20px'
            }
        },
        parts: ['selectedSortLink']
    },
    variants: {
        solid: {
            backgroundColor: 'primaryBlack',
            color: 'white',
            textTransform: 'uppercase',
            fontSize: 'sm',
            fontWeight: '700',
            // iOS-specific fix for Helvetica Neue vertical alignment
            flexDirection: 'column',
            lineHeight: '0',
            paddingTop: '0.25em',
            paddingBottom: '0.5em',
            '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                paddingTop: '0.6em',
                paddingBottom: '0.20em'
            },
            // For macOS Chrome
            '.is-mac-chrome &': {
                paddingTop: '0.5em',
                paddingBottom: '0.35em'
            },
            _hover: {
                bg: 'charcoal',
                textDecoration: 'none',
                _disabled: {
                    background: 'primaryBlack'
                }
            },
            _active: {bg: 'charcoal'}
        },
        secondary: {
            backgroundColor: 'white',
            padding: 2,
            color: 'black',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            position: 'relative',
            // iOS-specific fix for Helvetica Neue vertical alignment
            flexDirection: 'column',
            lineHeight: '0',
            paddingTop: '0.25em',
            paddingBottom: '0.5em',
            '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                paddingTop: '0.6em',
                paddingBottom: '0.20em'
            },
            // For macOS Chrome
            '.is-mac-chrome &': {
                paddingTop: '0.5em',
                paddingBottom: '0.35em'
            },
            _hover: {
                bg: 'gray.800'
            }
        },
        quickViewBtn: {
            backgroundColor: 'white',
            padding: 2,
            color: 'black',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            position: 'relative',
            '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                paddingTop: '0.6em',
                paddingBottom: '0.20em'
            },
            // For macOS Chrome
            '.is-mac-chrome &': {
                paddingTop: '0.5em',
                paddingBottom: '0.35em'
            },
            _hover: {
                bg: 'warmWhite'
            }
        },
        accountlink: {
            fontSize: 'md',
            fontWeight: 'normal',
            color: 'black',
            justifyContent: 'flex-start'
        },
        accountDashboardlink: {
            background: 'transparent',
            color: 'black',
            textTransform: 'capitalize',
            padding: 0,
            fontSize: 'md',
            fontWeight: 'normal',
            height: 'auto'
        },
        outline: (props) => ({
            color: 'black',
            fontSize: 'sm',
            backgroundColor: 'white',
            borderWidth: '1px',
            borderColor: props.colorScheme === 'black' ? 'gray.50' : 'black',
            textTransform: 'uppercase',
            // iOS-specific fix for Helvetica Neue vertical alignment
            flexDirection: 'column',
            lineHeight: '0',
            paddingTop: '0.25em',
            paddingBottom: '0.5em',
            '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                paddingTop: '0.6em',
                paddingBottom: '0.20em'
            },
            // For macOS Chrome
            '.is-mac-chrome &': {
                paddingTop: '0.5em',
                paddingBottom: '0.35em'
            },
            _hover: {
                bg: props.colorScheme === 'black' ? 'black' : 'gray.50'
            }
        }),
        footer: {
            fontSize: 'sm',
            backgroundColor: 'gray.100',
            color: 'black',
            _hover: {bg: 'gray.200'},
            _active: {bg: 'gray.300'},
            paddingLeft: 3,
            paddingRight: 3
        },
        link: (props) =>
            props.colorScheme === 'white'
                ? {
                      color: 'white',
                      fontWeight: 'normal',
                      textDecoration: 'underline',
                      minWidth: '1em',
                      lineHeight: 4,
                      _hover: {
                          color: 'white'
                      },
                      _focus: {
                          color: 'white'
                      }
                  }
                : {
                      color: 'black',
                      fontWeight: 'normal',
                      textDecoration: 'underline',
                      minWidth: '1em',
                      lineHeight: 4,
                      _hover: {
                          color: 'black'
                      },
                      _focus: {
                          color: 'black'
                      }
                  },
        'menu-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: {bg: 'gray.50', textDecoration: 'underline', textDecorationColor: 'gray.900'},
            _activeLink: {
                bg: 'gray.50',
                borderLeft: 'solid',
                borderLeftColor: 'gray.600',
                borderLeftWidth: '4px'
            }
        },
        'menu-link-mobile': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: {bg: 'gray.50', textDecoration: 'underline', textDecorationColor: 'gray.900'},
            _activeLink: {
                bg: 'gray.100',
                border: 'solid',
                borderColor: 'gray.600',
                borderWidth: '1px'
            }
        },
        'search-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: {textDecoration: 'none'}
        },
        'quantity-picker': {
            color: 'black',
            fontSize: 'sm',
            padding: 0,
            height: 'auto',
            minWidth: '20px',
            fontWeight: '400',
            _disabled: {
                opacity: 1
            }
        },
        filter: {
            display: 'flex',
            fontSize: '14px',
            fontWeight: 500,
            height: '26px',
            minHeight: '26px',
            padding: '6px 8px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
            border: '1px solid black',
            flexDirection: 'column',
            lineHeight: '0',
            paddingTop: '0.25em',
            paddingBottom: '0.5em',
            '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                paddingTop: '0.6em',
                paddingBottom: '0.20em'
            },
            // For macOS Chrome
            '.is-mac-chrome &': {
                paddingTop: '0.5em'
            },
            _hover: {
                background: 'black',
                color: 'white'
            }
        },
        filterLink: {
            color: 'black',
            fontSize: 'md',
            fontWeight: 500,
            borderBottom: '1px solid',
            borderColor: 'gray.50',
            paddingLeft: '32px',
            paddingRight: '32px',
            paddingTop: '20px',
            paddingBottom: '20px',
            height: 'auto'
        },
        selectedFilter: {
            display: 'flex',
            background: 'black',
            height: '26px',
            padding: '4px 8px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            color: 'white',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal'
        },
        sortSelect: {
            display: 'inline-flex',
            height: '24px',
            paddingLeft: '51px',
            paddingRight: '2px',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
            color: 'black',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal'
        },
        sortLink: {
            color: 'primaryBlack',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            height: 'auto',
            borderBottom: '1px solid',
            borderColor: 'gray.50',
            _hover: {
                background: 'grey.400',
                borderColor: 'grey.400'
            }
        },
        parts: ['sortLink', 'selectedSortLink']
    },
    sizes: {
        md: {
            height: 11,
            minWidth: 11
        }
    },
    defaultProps: {
        colorScheme: 'blue'
    }
}
